// src/router/index.tsx
import { lazy, Suspense, useEffect } from "react";
import { Switch, Route, useLocation, Redirect } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Footer from "../components/Footer";
import Header from "../components/Header";
import HeroBackground from "../components/HeroBackground";
import routes from "./config";
import { Styles } from "../styles/styles";
import { supportedLangs, isSupportedLanguage } from "../translation";

const Router = () => {
  const { i18n } = useTranslation();
  const location = useLocation();
  
  // Check if we're on the home page (with or without language prefix)
  const pathSegments = location.pathname.split('/').filter(Boolean);
  const firstSegment = pathSegments[0];
  const isLanguagePrefix = isSupportedLanguage(firstSegment);
  const isHomePage = 
    location.pathname === "/" || 
    location.pathname === "/home" || 
    (isLanguagePrefix && (pathSegments.length === 1 || pathSegments[1] === "home"));
    
  // Language detection from URL
  useEffect(() => {
    if (isLanguagePrefix && firstSegment !== i18n.language) {
      // Update the language based on URL if different from current
      i18n.changeLanguage(firstSegment);
      console.log('Setting language from URL:', firstSegment);
    }
  }, [firstSegment, i18n, isLanguagePrefix]);

  // Preload components
  const Home = lazy(() => import('../pages/Home'));

  return (
    <Suspense fallback={<div>Loading...</div>}>
      <Styles />
      {isHomePage && <HeroBackground />}
      <Header />
      <Switch>
        {/* Language-specific home routes */}
        {supportedLangs.map(lang => (
          <Route
            key={`${lang}-home`}
            path={`/${lang}`}
            exact
            component={Home}
          />
        ))}
        
        {/* Language-specific content routes */}
        {supportedLangs.map(lang => 
          routes.map(route => {
            const Component = lazy(() => import(`../pages/${route.component}`));
            return (
              <Route
                key={`${lang}-${route.component}`}
                path={`/${lang}${route.path}`}
                exact={route.exact}
                component={Component}
              />
            );
          })
        )}
        
        {/* Default routes without language prefix */}
        {routes.map((route) => {
          const Component = lazy(() => import(`../pages/${route.component}`));
          return (
            <Route
              key={route.component}
              path={route.path}
              exact={route.exact}
              component={Component}
            />
          );
        })}
        
        {/* Fallback redirect for unsupported routes */}
        <Redirect to="/" />
      </Switch>
      <Footer />
    </Suspense>
  );
};

export default Router;